((root, factory) => {
  const api = factory();

  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) {
    root.RDFeedingCalculator = api;
    api.register(root);
  }
})(typeof window !== 'undefined' ? window : null, () => {
  const LB_PER_KG = 2.20462262185;
  const GRAMS_PER_LB = 453.59237;
  const STORAGE_KEY = 'raw-dawg-feeding-calculator:v1';
  const STORAGE_VERSION = 2;
  const STORAGE_TTL = 30 * 24 * 60 * 60 * 1000;
  const DEFAULT_PACK_SIZES = [8, 14, 30];
  const DEFAULT_CADENCE_WEEKS = [4, 2];
  const LOWER_ORDER_TOLERANCE = 0.15;
  const FLOATING_POINT_TOLERANCE = 0.000001;
  const BODY_CONDITION_RATES = {
    underweight: 0.03,
    ideal: 0.025,
    overweight: 0.02
  };
  const ACTIVITY_RATE_ADJUSTMENTS = {
    low: -0.0025,
    moderate: 0,
    high: 0.0025
  };
  const BODY_CONDITION_RANGE_DELTA = 0.0025;
  const WEIGHT_BOUNDS = {
    lb: { min: 2, max: 250 },
    kg: { min: 0.9, max: 113.4 }
  };
  const DEFAULT_TRANSITION_PHASES = [
    { label: 'Days 1–3', gentleLabel: 'Days 1–4', rawPercent: 25 },
    { label: 'Days 4–6', gentleLabel: 'Days 5–7', rawPercent: 50 },
    { label: 'Days 7–9', gentleLabel: 'Days 8–11', rawPercent: 75 },
    { label: 'Day 10+', gentleLabel: 'Days 12–14', rawPercent: 100 }
  ];

  const formatPackOrderLabel = (quantity, size) => {
    const boxLabel = Number(quantity) === 1 ? 'box' : 'boxes';
    return `${quantity} × ${size} lb ${boxLabel}`;
  };

  const roundUpTenth = (value) => {
    const scaled = Number(value) * 10;
    const floatingPointTolerance = Number.EPSILON * Math.max(1, Math.abs(scaled)) * 8;
    return Math.ceil(scaled - floatingPointTolerance) / 10;
  };

  const convertToPounds = (weight, unit = 'lb') => {
    const numericWeight = Number(weight);
    if (!Number.isFinite(numericWeight) || numericWeight <= 0) {
      throw new RangeError('Weight must be a positive number.');
    }
    if (!['lb', 'kg'].includes(unit)) {
      throw new RangeError('Weight unit must be lb or kg.');
    }
    return unit === 'kg' ? numericWeight * LB_PER_KG : numericWeight;
  };

  const calculateDog = ({ weight, unit = 'lb', bodyCondition = 'ideal', activity = 'moderate' }) => {
    const weightLb = convertToPounds(weight, unit);
    const numericWeight = Number(weight);
    const bounds = WEIGHT_BOUNDS[unit];
    if (numericWeight < bounds.min || numericWeight > bounds.max) {
      throw new RangeError(`Weight must be between ${bounds.min} and ${bounds.max} ${unit}.`);
    }
    const baseRate = BODY_CONDITION_RATES[bodyCondition];
    const activityAdjustment = ACTIVITY_RATE_ADJUSTMENTS[activity];
    if (!Number.isFinite(baseRate)) throw new RangeError('Body condition must be underweight, ideal, or overweight.');
    if (!Number.isFinite(activityAdjustment)) throw new RangeError('Activity must be low, moderate, or high.');
    const rate = baseRate + activityAdjustment;
    const dailyLowLb = weightLb * (baseRate - BODY_CONDITION_RANGE_DELTA);
    const dailyStartLb = weightLb * rate;
    const dailyHighLb = weightLb * (baseRate + BODY_CONDITION_RANGE_DELTA);

    return {
      weightLb,
      bodyCondition,
      activity,
      baseRate,
      activityAdjustment,
      rate,
      dailyLowLb,
      dailyStartLb,
      dailyHighLb,
      dailyLowOz: dailyLowLb * 16,
      dailyStartOz: dailyStartLb * 16,
      dailyHighOz: dailyHighLb * 16
    };
  };

  const normalizeStoredDog = (values = {}) => ({
    name: String(values.name || '').slice(0, 40),
    weight: values.weight === 0 ? '0' : String(values.weight || ''),
    unit: values.unit === 'kg' ? 'kg' : 'lb',
    bodyCondition: Object.hasOwn(BODY_CONDITION_RATES, values.bodyCondition) ? values.bodyCondition : '',
    activity: Object.hasOwn(ACTIVITY_RATE_ADJUSTMENTS, values.activity) ? values.activity : ''
  });

  const comparePlans = (candidate, current) => {
    if (!current) return -1;
    if (candidate.count !== current.count) return candidate.count - current.count;
    if (Math.abs(candidate.overage - current.overage) > 0.000001) {
      return candidate.overage - current.overage;
    }

    const thirtyDifference = (current.quantities[30] || 0) - (candidate.quantities[30] || 0);
    if (thirtyDifference !== 0) return thirtyDifference;

    const fourteenDifference = (current.quantities[14] || 0) - (candidate.quantities[14] || 0);
    if (fourteenDifference !== 0) return fourteenDifference;

    return (candidate.quantities[8] || 0) - (current.quantities[8] || 0);
  };

  const optimizeBoxes = (requiredPounds, packSizes = DEFAULT_PACK_SIZES) => {
    const required = Number(requiredPounds);
    if (!Number.isFinite(required) || required <= 0) {
      throw new RangeError('Required pounds must be a positive number.');
    }

    const sizes = [...new Set(packSizes.map(Number).filter((size) => Number.isFinite(size) && size > 0))]
      .sort((a, b) => b - a);
    if (sizes.length === 0) throw new RangeError('At least one pack size is required.');

    let best = null;
    const lowerPlansByCount = new Map();
    const quantities = Object.fromEntries(sizes.map((size) => [size, 0]));

    const search = (index, total, count) => {
      if (index === sizes.length) {
        if (count === 0) return;
        const candidate = {
          total,
          count,
          overage: Math.max(0, total - required),
          shortfall: Math.max(0, required - total),
          quantities: { ...quantities }
        };
        if (candidate.shortfall <= FLOATING_POINT_TOLERANCE) {
          if (comparePlans(candidate, best) < 0) best = candidate;
        } else {
          const currentLowerPlan = lowerPlansByCount.get(count);
          const isCloserLowerPlan = !currentLowerPlan
            || candidate.total > currentLowerPlan.total + FLOATING_POINT_TOLERANCE;
          const isPreferredEqualPlan = currentLowerPlan
            && Math.abs(candidate.total - currentLowerPlan.total) <= FLOATING_POINT_TOLERANCE
            && comparePlans(candidate, currentLowerPlan) < 0;
          if (isCloserLowerPlan || isPreferredEqualPlan) lowerPlansByCount.set(count, candidate);
        }
        return;
      }

      const size = sizes[index];
      const maxCount = Math.ceil(required / size) + 1;
      for (let quantity = 0; quantity <= maxCount; quantity += 1) {
        quantities[size] = quantity;
        search(index + 1, total + (quantity * size), count + quantity);
      }
      quantities[size] = 0;
    };

    search(0, 0, 0);

    const lowerPlan = [...lowerPlansByCount.values()]
      .filter((candidate) => candidate.count <= best.count)
      .reduce((current, candidate) => {
        if (!current || candidate.total > current.total + FLOATING_POINT_TOLERANCE) return candidate;
        if (Math.abs(candidate.total - current.total) <= FLOATING_POINT_TOLERANCE
          && comparePlans(candidate, current) < 0) return candidate;
        return current;
      }, null);

    const isWithinLowerOrderTolerance = lowerPlan
      && required <= (lowerPlan.total * (1 + LOWER_ORDER_TOLERANCE)) + FLOATING_POINT_TOLERANCE;

    return isWithinLowerOrderTolerance ? lowerPlan : best;
  };

  const optimizeSingleBox = (requiredPounds, packSizes = DEFAULT_PACK_SIZES) => {
    const required = Number(requiredPounds);
    const sizes = [...new Set(packSizes.map(Number).filter((size) => Number.isFinite(size) && size > 0))]
      .sort((a, b) => a - b);
    const size = sizes.find((candidate) => (
      required <= (candidate * (1 + LOWER_ORDER_TOLERANCE)) + FLOATING_POINT_TOLERANCE
    ));
    if (!size) return null;

    const quantities = Object.fromEntries(sizes.map((candidate) => [candidate, candidate === size ? 1 : 0]));
    return {
      total: size,
      count: 1,
      overage: Math.max(0, size - required),
      shortfall: Math.max(0, required - size),
      quantities
    };
  };

  const calculateHousehold = (dogs, packSizes = DEFAULT_PACK_SIZES) => {
    if (!Array.isArray(dogs) || dogs.length === 0) {
      throw new RangeError('At least one dog is required.');
    }

    const dogPlans = dogs.map(calculateDog);
    const dailyLowLb = dogPlans.reduce((sum, dog) => sum + dog.dailyLowLb, 0);
    const dailyStartLb = dogPlans.reduce((sum, dog) => sum + dog.dailyStartLb, 0);
    const dailyHighLb = dogPlans.reduce((sum, dog) => sum + dog.dailyHighLb, 0);
    const need28Lb = roundUpTenth(dailyStartLb * 28);
    let packPlan = null;

    DEFAULT_CADENCE_WEEKS.some((cadenceWeeks) => {
      const cadenceNeedLb = need28Lb * (cadenceWeeks / 4);
      const shipmentPlan = optimizeSingleBox(cadenceNeedLb, packSizes);
      if (!shipmentPlan) return false;

      const equivalent28Lb = shipmentPlan.total * (4 / cadenceWeeks);
      packPlan = {
        ...shipmentPlan,
        cadenceWeeks,
        equivalent28Lb,
        overage: Math.max(0, equivalent28Lb - need28Lb),
        shortfall: Math.max(0, need28Lb - equivalent28Lb)
      };
      return true;
    });

    if (!packPlan) {
      const fourWeekPlan = optimizeBoxes(need28Lb, packSizes);
      packPlan = { ...fourWeekPlan, cadenceWeeks: 4, equivalent28Lb: fourWeekPlan.total };
    }

    const daysNotCovered = packPlan.shortfall > FLOATING_POINT_TOLERANCE
      ? Math.ceil((packPlan.shortfall / dailyStartLb) - FLOATING_POINT_TOLERANCE)
      : 0;

    return {
      dogs: dogPlans,
      dailyLowLb,
      dailyStartLb,
      dailyHighLb,
      dailyLowOz: dailyLowLb * 16,
      dailyStartOz: dailyStartLb * 16,
      dailyHighOz: dailyHighLb * 16,
      need28Lb,
      need28Kg: need28Lb / LB_PER_KG,
      packPlan: { ...packPlan, daysNotCovered }
    };
  };

  const formatPackSignature = (quantities) => [30, 14, 8]
    .filter((size) => Number(quantities?.[size] || 0) > 0)
    .map((size) => `${size}x${Number(quantities[size])}`)
    .join('_');

  const needBucket = (pounds) => {
    if (pounds < 14) return 'under_14';
    if (pounds <= 30) return '14_to_30';
    if (pounds <= 60) return '31_to_60';
    return 'over_60';
  };

  const formatNumber = (value, maximumFractionDigits = 1) => new Intl.NumberFormat('en-US', {
    maximumFractionDigits,
    minimumFractionDigits: 0
  }).format(value);

  const dogCountBucket = (count) => count >= 3 ? '3_plus' : String(count);

  const calculateTransitionAmounts = (dailyStartOz, phases) => {
    const dailyAmount = Number(dailyStartOz);
    if (!Number.isFinite(dailyAmount) || dailyAmount <= 0) {
      throw new RangeError('Daily starting ounces must be a positive number.');
    }

    const sourcePhases = phases === undefined ? DEFAULT_TRANSITION_PHASES : phases;
    const normalizedPhases = Array.isArray(sourcePhases)
      ? sourcePhases
        .map((phase) => ({
          label: String(phase?.label || '').trim(),
          rawPercent: Number(phase?.rawPercent)
        }))
        .filter((phase) => phase.label && Number.isFinite(phase.rawPercent) && phase.rawPercent >= 0 && phase.rawPercent <= 100)
      : [];

    return normalizedPhases.map((phase) => ({
      ...phase,
      ounces: dailyAmount * (phase.rawPercent / 100)
    }));
  };

  const calculateTransitionPlan = (dailyStartOz, phases, pace = 'standard') => {
    const dailyAmount = Number(dailyStartOz);
    if (!Number.isFinite(dailyAmount) || dailyAmount <= 0) {
      throw new RangeError('Daily starting ounces must be a positive number.');
    }

    const sourcePhases = phases === undefined ? DEFAULT_TRANSITION_PHASES : phases;
    const normalizedPhases = Array.isArray(sourcePhases)
      ? sourcePhases
        .map((phase, sourceIndex) => ({
          sourceIndex,
          label: String(phase?.label || '').trim(),
          gentleLabel: String(phase?.gentleLabel || '').trim(),
          rawPercent: Number(phase?.rawPercent)
        }))
        .filter((phase) => Number.isFinite(phase.rawPercent) && phase.rawPercent >= 0 && phase.rawPercent <= 100)
      : [];

    return normalizedPhases.map((phase) => {
      const ounces = dailyAmount * (phase.rawPercent / 100);
      const paceLabel = pace === 'gentle'
        ? phase.gentleLabel || phase.label
        : phase.label || phase.gentleLabel;
      return {
        sourceIndex: phase.sourceIndex,
        label: paceLabel || `Phase ${phase.sourceIndex + 1}`,
        rawPercent: phase.rawPercent,
        currentFoodPercent: 100 - phase.rawPercent,
        ounces,
        grams: (ounces / 16) * GRAMS_PER_LB,
        perMealOunces: ounces / 2
      };
    });
  };

  const register = (browserWindow) => {
    if (!browserWindow?.customElements || browserWindow.customElements.get('rd-feeding-calculator')) return;

    class RDFeedingCalculatorElement extends browserWindow.HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') {
          this.bindConsentListener();
          this.ensurePrivacyApi();
          return;
        }
        this.dataset.initialized = 'true';

        this.config = this.readConfig();
        this.form = this.querySelector('[data-rd-calculator-form]');
        this.dogList = this.querySelector('[data-rd-calculator-dogs]');
        this.dogTemplate = this.querySelector('[data-rd-calculator-dog-template]');
        this.errorSummary = this.querySelector('[data-rd-calculator-errors]');
        this.resultPanel = this.querySelector('[data-rd-calculator-result]');
        this.resultEmpty = this.querySelector('[data-rd-calculator-result-empty]');
        this.resultPlan = this.querySelector('[data-rd-calculator-result-plan]');
        this.resultHeading = this.querySelector('[data-rd-calculator-result-heading]');
        this.liveStatus = this.querySelector('[data-rd-calculator-live]');
        this.addButton = this.querySelector('[data-rd-calculator-add]');
        this.submitButton = this.querySelector('[data-rd-calculator-submit]');
        this.resetButton = this.querySelector('[data-rd-calculator-reset]');
        this.storageNote = this.querySelector('[data-rd-calculator-storage-note]');
        this.transitionDetails = this.querySelector('[data-rd-calculator-transition]');
        this.transitionSummaryText = this.querySelector('[data-rd-calculator-transition-label]');
        this.transitionAmounts = this.querySelector('[data-rd-calculator-transition-amounts]');
        this.transitionDogSelect = this.querySelector('[data-rd-calculator-transition-dog]');
        this.transitionPaceSelect = this.querySelector('[data-rd-calculator-transition-pace]');
        this.transitionStepCards = Array.from(this.querySelectorAll('[data-rd-calculator-transition-step]'));
        this.transitionEmpty = this.querySelector('[data-rd-calculator-transition-empty]');
        this.transitionPlan = this.querySelector('[data-rd-calculator-transition-plan]');
        this.transitionPlanSummary = this.querySelector('[data-rd-calculator-transition-summary]');
        this.transitionResults = [];
        this.transitionPaceInitialized = false;
        this.hasCalculated = false;
        this.startedTracked = false;
        this.nextDogId = 1;
        this.recalculateTimer = null;
        this.dogs = this.restoreState() || [this.createDog()];

        this.renderDogList();
        this.updateStorageNote();
        this.bindEvents();
        this.updateTransitionToggleLabel();
        this.ensurePrivacyApi();
        this.showEmptyResult(this.config.emptyState || 'Enter your dog’s details to see a plan.');
      }

      disconnectedCallback() {
        browserWindow.clearTimeout(this.recalculateTimer);
        if (this.consentListenerAttached && this.handleConsentCollected) {
          browserWindow.document.removeEventListener('visitorConsentCollected', this.handleConsentCollected);
          this.consentListenerAttached = false;
        }
      }

      readConfig() {
        try {
          return JSON.parse(this.querySelector('[data-rd-calculator-config]')?.textContent || '{}');
        } catch (_error) {
          return {};
        }
      }

      createDog(values = {}) {
        const dog = {
          id: `dog-${Date.now()}-${this.nextDogId}`,
          ...normalizeStoredDog(values)
        };
        this.nextDogId += 1;
        return dog;
      }

      bindEvents() {
        this.form?.addEventListener('submit', (event) => {
          event.preventDefault();
          this.syncStateFromDom();
          this.trackStarted();
          this.calculate({ focusResult: true, trackCompletion: true, showErrors: true });
        });

        this.form?.addEventListener('input', (event) => {
          if (!event.target.matches('input')) return;
          // Unit radios are handled on `change` so the previous unit remains
          // available while the entered weight is converted.
          if (event.target.matches('[data-rd-calculator-unit]')) return;
          this.trackStarted();
          this.syncStateFromDom();
          this.saveState();
          this.updateSubmitLabel();
          this.scheduleRecalculation();
        });

        this.form?.addEventListener('change', (event) => {
          if (event.target.matches('[data-rd-calculator-unit]')) {
            this.convertDogUnit(event.target.closest('[data-rd-calculator-dog]'), event.target.value);
          } else {
            this.syncStateFromDom();
          }
          this.trackStarted();
          this.saveState();
          this.updateSubmitLabel();
          this.scheduleRecalculation();
        });

        this.addButton?.addEventListener('click', () => this.addDog());
        this.resetButton?.addEventListener('click', () => this.reset());

        this.dogList?.addEventListener('click', (event) => {
          const removeButton = event.target.closest('[data-rd-calculator-remove]');
          if (removeButton) this.removeDog(removeButton.closest('[data-rd-calculator-dog]'));
        });

        this.transitionDetails?.addEventListener('toggle', () => {
          this.updateTransitionToggleLabel();
        });

        this.transitionDogSelect?.addEventListener('change', () => this.renderSelectedTransitionPlan());
        this.transitionPaceSelect?.addEventListener('change', () => this.renderSelectedTransitionPlan());

        this.bindConsentListener();
      }

      bindConsentListener() {
        if (this.consentListenerAttached) return;
        this.handleConsentCollected ||= () => this.saveState();
        browserWindow.document.addEventListener('visitorConsentCollected', this.handleConsentCollected);
        this.consentListenerAttached = true;
      }

      ensurePrivacyApi() {
        if (!browserWindow.Shopify) return;
        if (typeof browserWindow.Shopify.customerPrivacy?.preferencesProcessingAllowed === 'function') {
          if (!this.storageAllowed()) this.saveState();
          return;
        }
        if (typeof browserWindow.Shopify.loadFeatures !== 'function') return;

        try {
          browserWindow.Shopify.loadFeatures(
            [{ name: 'consent-tracking-api', version: '0.1' }],
            (error) => {
              if (error || this.isConnected === false) return;
              if (!this.storageAllowed()) {
                this.saveState();
                return;
              }

              const isPristine = this.dogs.length === 1
                && !this.dogs[0].name
                && !this.dogs[0].weight
                && !this.dogs[0].bodyCondition
                && !this.dogs[0].activity
                && this.dogs[0].unit === 'lb';
              const restored = isPristine ? this.restoreState() : null;
              if (restored) {
                this.dogs = restored;
                this.renderDogList();
              }
              this.updateStorageNote();
            }
          );
        } catch (_error) {
          // The calculator stays usable if Shopify's privacy feature cannot load.
        }
      }

      renderDogList() {
        if (!this.dogList || !this.dogTemplate) return;
        this.dogList.replaceChildren();

        this.dogs.forEach((dog, index) => {
          const fragment = this.dogTemplate.content.cloneNode(true);
          const card = fragment.querySelector('[data-rd-calculator-dog]');
          const legend = fragment.querySelector('[data-rd-calculator-dog-legend]');
          const nameInput = fragment.querySelector('[data-rd-calculator-name]');
          const weightInput = fragment.querySelector('[data-rd-calculator-weight]');
          const removeButton = fragment.querySelector('[data-rd-calculator-remove]');
          const prefix = `RDFood-${this.config.sectionId || 'section'}-${dog.id}`;

          card.dataset.dogId = dog.id;
          legend.textContent = dog.name ? `${dog.name}’s details` : `Dog ${index + 1}`;

          nameInput.id = `${prefix}-name`;
          nameInput.value = dog.name;
          nameInput.setAttribute('name', `dogs[${index}][name]`);
          fragment.querySelector('[data-rd-calculator-name-label]').setAttribute('for', nameInput.id);

          weightInput.id = `${prefix}-weight`;
          weightInput.value = dog.weight;
          weightInput.setAttribute('name', `dogs[${index}][weight]`);
          fragment.querySelector('[data-rd-calculator-weight-label]').setAttribute('for', weightInput.id);
          const weightError = fragment.querySelector('[data-rd-calculator-weight-error]');
          weightError.id = `${prefix}-weight-error`;
          weightInput.setAttribute('aria-describedby', weightError.id);

          fragment.querySelectorAll('[data-rd-calculator-unit]').forEach((radio) => {
            radio.name = `${prefix}-unit`;
            radio.id = `${prefix}-unit-${radio.value}`;
            radio.checked = radio.value === dog.unit;
            radio.nextElementSibling?.setAttribute('for', radio.id);
          });

          fragment.querySelectorAll('[data-rd-calculator-body-condition]').forEach((radio) => {
            radio.name = `${prefix}-body-condition`;
            radio.id = `${prefix}-body-condition-${radio.value}`;
            radio.checked = radio.value === dog.bodyCondition;
            radio.nextElementSibling?.setAttribute('for', radio.id);
          });
          const bodyConditionError = fragment.querySelector('[data-rd-calculator-body-condition-error]');
          bodyConditionError.id = `${prefix}-body-condition-error`;
          const bodyConditionGroup = fragment.querySelector('[data-rd-calculator-body-condition-group]');
          bodyConditionGroup.setAttribute('aria-describedby', bodyConditionError.id);

          fragment.querySelectorAll('[data-rd-calculator-activity]').forEach((radio) => {
            radio.name = `${prefix}-activity`;
            radio.id = `${prefix}-activity-${radio.value}`;
            radio.checked = radio.value === dog.activity;
            radio.nextElementSibling?.setAttribute('for', radio.id);
          });
          const activityError = fragment.querySelector('[data-rd-calculator-activity-error]');
          activityError.id = `${prefix}-activity-error`;
          const activityGroup = fragment.querySelector('[data-rd-calculator-activity-group]');
          activityGroup.setAttribute('aria-describedby', activityError.id);

          removeButton.hidden = this.dogs.length === 1;
          removeButton.setAttribute('aria-label', `Remove ${dog.name || `Dog ${index + 1}`}`);

          this.dogList.appendChild(fragment);
        });

        this.addButton.disabled = this.dogs.length >= Number(this.config.maxDogs || 8);
        this.updateSubmitLabel();
      }

      syncStateFromDom() {
        this.dogs = Array.from(this.dogList.querySelectorAll('[data-rd-calculator-dog]')).map((card, index) => {
          const existing = this.dogs.find((dog) => dog.id === card.dataset.dogId) || this.createDog();
          const name = card.querySelector('[data-rd-calculator-name]')?.value.trim() || '';
          const weight = card.querySelector('[data-rd-calculator-weight]')?.value.trim() || '';
          const unit = card.querySelector('[data-rd-calculator-unit]:checked')?.value || existing.unit || 'lb';
          const bodyCondition = card.querySelector('[data-rd-calculator-body-condition]:checked')?.value || '';
          const activity = card.querySelector('[data-rd-calculator-activity]:checked')?.value || '';
          card.querySelector('[data-rd-calculator-dog-legend]').textContent = name ? `${name}’s details` : `Dog ${index + 1}`;
          return { ...existing, name: name.slice(0, 40), weight, unit, bodyCondition, activity };
        });
      }

      convertDogUnit(card, nextUnit) {
        if (!card) return;
        const dog = this.dogs.find((item) => item.id === card.dataset.dogId);
        const weightInput = card.querySelector('[data-rd-calculator-weight]');
        const currentWeight = Number(weightInput?.value);
        if (dog && Number.isFinite(currentWeight) && currentWeight > 0 && dog.unit !== nextUnit) {
          const converted = nextUnit === 'kg' ? currentWeight / LB_PER_KG : currentWeight * LB_PER_KG;
          weightInput.value = formatNumber(converted, 2).replaceAll(',', '');
        }
        this.syncStateFromDom();
      }

      addDog() {
        this.syncStateFromDom();
        if (this.dogs.length >= Number(this.config.maxDogs || 8)) return;
        const dog = this.createDog();
        this.dogs.push(dog);
        this.hasCalculated = false;
        this.renderDogList();
        this.saveState();
        this.showEmptyResult('Complete the new dog’s details to refresh the household plan.');
        const newCard = this.dogList.querySelector(`[data-dog-id="${dog.id}"]`);
        newCard?.querySelector('[data-rd-calculator-name]')?.focus();
        this.announce(`Dog ${this.dogs.length} added.`);
      }

      removeDog(card) {
        if (!card || this.dogs.length === 1) return;
        this.syncStateFromDom();
        const removedIndex = this.dogs.findIndex((dog) => dog.id === card.dataset.dogId);
        const removedDog = this.dogs[removedIndex];
        this.dogs.splice(removedIndex, 1);
        this.renderDogList();
        this.saveState();

        if (this.hasCalculated) this.calculate({ focusResult: false, trackCompletion: false, showErrors: false });
        const focusIndex = Math.min(removedIndex, this.dogs.length - 1);
        this.dogList.querySelectorAll('[data-rd-calculator-dog]')[focusIndex]
          ?.querySelector('[data-rd-calculator-name]')?.focus();
        this.announce(`${removedDog?.name || `Dog ${removedIndex + 1}`} removed.`);
      }

      updateSubmitLabel() {
        const label = this.submitButton?.querySelector('[data-rd-calculator-submit-label]');
        if (!label) return;
        const firstName = this.dogs[0]?.name.trim();
        label.textContent = this.dogs.length > 1
          ? 'Calculate household plan'
          : firstName ? `Calculate ${firstName}’s plan` : 'Calculate feeding plan';
      }

      scheduleRecalculation() {
        if (!this.hasCalculated) return;
        browserWindow.clearTimeout(this.recalculateTimer);
        this.recalculateTimer = browserWindow.setTimeout(() => {
          this.calculate({ focusResult: false, trackCompletion: false, showErrors: false });
        }, 220);
      }

      validateDogs({ showErrors = true } = {}) {
        if (showErrors) this.clearErrors();
        const errors = [];

        this.dogs.forEach((dog, index) => {
          const card = this.dogList.querySelector(`[data-dog-id="${dog.id}"]`);
          const weightInput = card?.querySelector('[data-rd-calculator-weight]');
          const bodyConditionGroup = card?.querySelector('[data-rd-calculator-body-condition-group]');
          const activityGroup = card?.querySelector('[data-rd-calculator-activity-group]');
          const displayName = dog.name || `Dog ${index + 1}`;
          const numericWeight = Number(dog.weight);
          const minimum = dog.unit === 'kg' ? 0.9 : 2;
          const maximum = dog.unit === 'kg' ? 113.4 : 250;

          if (!dog.weight || !Number.isFinite(numericWeight)) {
            errors.push({ field: weightInput, message: `Enter ${displayName}’s weight as a number.` });
          } else if (numericWeight < minimum || numericWeight > maximum) {
            errors.push({
              field: weightInput,
              message: `${displayName}’s weight must be between ${minimum} and ${maximum} ${dog.unit}.`
            });
          }

          if (!Object.hasOwn(BODY_CONDITION_RATES, dog.bodyCondition)) {
            errors.push({
              field: bodyConditionGroup,
              message: `Choose ${displayName}’s body condition.`
            });
          }
          if (!Object.hasOwn(ACTIVITY_RATE_ADJUSTMENTS, dog.activity)) {
            errors.push({
              field: activityGroup,
              message: `Choose ${displayName}’s activity level.`
            });
          }
        });

        if (showErrors && errors.length > 0) this.showErrors(errors);
        return { errors };
      }

      clearErrors() {
        this.errorSummary.hidden = true;
        this.errorSummary.querySelector('[data-rd-calculator-error-list]')?.replaceChildren();
        this.dogList.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));
        this.dogList.querySelectorAll('[data-rd-calculator-field-error]').forEach((error) => {
          error.hidden = true;
          error.textContent = '';
        });
      }

      showErrors(errors) {
        const list = this.errorSummary.querySelector('[data-rd-calculator-error-list]');
        list.replaceChildren();

        errors.forEach(({ field, message }) => {
          if (!field.id) field.id = `RDFoodError-${Math.random().toString(36).slice(2, 8)}`;
          field.setAttribute('aria-invalid', 'true');
          const card = field.closest('[data-rd-calculator-dog]');
          const inlineError = field.matches('[data-rd-calculator-weight]')
            ? card?.querySelector('[data-rd-calculator-weight-error]')
            : field.matches('[data-rd-calculator-body-condition-group]')
              ? card?.querySelector('[data-rd-calculator-body-condition-error]')
              : card?.querySelector('[data-rd-calculator-activity-error]');
          if (inlineError) {
            inlineError.textContent = message;
            inlineError.hidden = false;
          }

          const item = browserWindow.document.createElement('li');
          const link = browserWindow.document.createElement('a');
          link.href = `#${field.id}`;
          link.textContent = message;
          item.appendChild(link);
          list.appendChild(item);
        });

        this.errorSummary.hidden = false;
      }

      calculate({ focusResult = false, trackCompletion = false, showErrors = true } = {}) {
        const validation = this.validateDogs({ showErrors });

        if (validation.errors.length > 0) {
          this.hasCalculated = false;
          this.showEmptyResult('Fix the highlighted details to see an updated plan.');
          if (focusResult) this.errorSummary.focus();
          return;
        }

        const dogResults = this.dogs.map((dog) => ({ ...calculateDog(dog), dog }));
        this.renderPerDogResults(dogResults);

        const plan = calculateHousehold(this.dogs, this.config.packSizes || DEFAULT_PACK_SIZES);
        this.hasCalculated = true;
        this.renderPlan(plan, dogResults);
        this.saveState();
        if (trackCompletion) this.trackCompleted(plan);
        if (focusResult) this.resultHeading.focus();
        this.announce(`Plan calculated for ${this.householdName()}.`);
      }

      renderPerDogResults(results) {
        this.dogList.querySelectorAll('[data-rd-calculator-dog-result]').forEach((result) => { result.hidden = true; });

        results.forEach(({ dog, ...calculated }) => {
          const card = this.dogList.querySelector(`[data-dog-id="${dog.id}"]`);
          const result = card?.querySelector('[data-rd-calculator-dog-result]');
          if (!result) return;
          const metric = this.dailyDisplay(calculated, dog.unit);
          result.querySelector('[data-rd-calculator-dog-range]').textContent = metric.range;
          result.querySelector('[data-rd-calculator-dog-start]').textContent = `Start at ${metric.start}`;
          result.hidden = false;
        });
      }

      renderPlan(plan, eligibleResults) {
        this.resultEmpty.hidden = true;
        this.resultPlan.hidden = false;

        const oneDog = this.dogs.length === 1;
        const firstName = this.dogs[0]?.name || 'Your dog';
        this.resultHeading.textContent = oneDog ? `${firstName}’s feeding-plan estimate` : 'Your household feeding-plan estimate';
        this.querySelector('[data-rd-calculator-daily-range]').textContent = `${formatNumber(plan.dailyLowOz)}–${formatNumber(plan.dailyHighOz)} oz`;
        this.querySelector('[data-rd-calculator-daily-start]').textContent = `${formatNumber(plan.dailyStartOz)} oz`;
        this.querySelector('[data-rd-calculator-per-meal]').textContent = `${formatNumber(plan.dailyStartOz / 2)} oz per meal`;
        this.querySelector('[data-rd-calculator-daily-metric]').textContent = `${formatNumber(plan.dailyStartLb * GRAMS_PER_LB)} g per day`;
        this.querySelector('[data-rd-calculator-need]').textContent = `${formatNumber(plan.need28Lb)} lb`;
        this.querySelector('[data-rd-calculator-need-metric]').textContent = `${formatNumber(plan.need28Kg)} kg`;
        const cadenceWeeks = plan.packPlan.cadenceWeeks || 4;
        const equivalent28Lb = plan.packPlan.equivalent28Lb ?? plan.packPlan.total;
        this.querySelector('[data-rd-calculator-delivered]').textContent = `${formatNumber(equivalent28Lb)} lb delivered per 28 days`;
        const orderCoverage = this.querySelector('[data-rd-calculator-overage]');
        if (plan.packPlan.shortfall > FLOATING_POINT_TOLERANCE) {
          const days = plan.packPlan.daysNotCovered;
          const dayCoverage = days === 1 ? 'day is' : 'days are';
          const recommendation = plan.packPlan.count === 1 ? 'This one-box recommendation' : 'This recommendation';
          orderCoverage.textContent = `${recommendation} is ${formatNumber(plan.packPlan.shortfall)} lb short of the 28-day estimate. About ${days} ${dayCoverage} not covered.`;
        } else if (plan.packPlan.overage < FLOATING_POINT_TOLERANCE) {
          orderCoverage.textContent = 'Exactly covers the 28-day estimate.';
        } else {
          const shipmentCopy = plan.packPlan.count === 1
            ? 'This recommendation keeps each shipment to one box.'
            : 'This recommendation uses the fewest available boxes.';
          orderCoverage.textContent = `${formatNumber(plan.packPlan.overage)} lb beyond the 28-day estimate. ${shipmentCopy}`;
        }

        const packList = this.querySelector('[data-rd-calculator-pack-list]');
        const packVisualTemplate = this.querySelector('[data-rd-calculator-pack-visual-template]');
        packList.replaceChildren();
        [30, 14, 8].forEach((size) => {
          const quantity = Number(plan.packPlan.quantities[size] || 0);
          if (!quantity) return;
          const item = browserWindow.document.createElement('li');
          const label = browserWindow.document.createElement('strong');
          const visual = packVisualTemplate?.content?.firstElementChild?.cloneNode(true);

          item.dataset.packSize = String(size);
          label.className = 'rd-calc__pack-copy';
          label.textContent = `${formatPackOrderLabel(quantity, size)} every ${cadenceWeeks} weeks`;

          if (visual) {
            visual.dataset.packSize = String(size);
            visual.querySelector('[data-rd-calculator-pack-visual-size]').textContent = `${size} lb`;
            item.append(visual);
          }
          item.append(label);
          packList.appendChild(item);
        });

        this.renderTransitionAmounts(eligibleResults);
      }

      showEmptyResult(message) {
        this.resultPlan.hidden = true;
        this.resultEmpty.hidden = false;
        this.resultHeading.textContent = 'Your feeding plan';
        this.resultEmpty.querySelector('[data-rd-calculator-empty-copy]').textContent = message;
        this.clearTransitionPlanner({ resetPace: true });
        this.dogList?.querySelectorAll('[data-rd-calculator-dog-result]').forEach((result) => { result.hidden = true; });
      }

      renderTransitionAmounts(results) {
        if (this.transitionDogSelect && this.transitionPlan) {
          this.renderTransitionPlanner(results);
          return;
        }

        this.renderLegacyTransitionAmounts(results);
      }

      renderTransitionPlanner(results) {
        this.transitionResults = Array.isArray(results) ? results.filter((result) => result?.dog) : [];
        if (!this.transitionResults.length) {
          this.clearTransitionPlanner({ resetPace: true });
          return;
        }

        const previousDogId = this.transitionDogSelect?.value;
        const eligibleDogIds = new Set(this.transitionResults.map(({ dog }) => dog.id));
        const selectedDogId = eligibleDogIds.has(previousDogId)
          ? previousDogId
          : this.transitionResults[0].dog.id;

        if (this.transitionDogSelect?.replaceChildren) {
          this.transitionDogSelect.replaceChildren();
          this.transitionResults.forEach(({ dog }, index) => {
            const option = browserWindow.document.createElement('option');
            option.value = dog.id;
            option.textContent = dog.name || `Dog ${index + 1}`;
            this.transitionDogSelect.appendChild(option);
          });
        }
        if (this.transitionDogSelect) {
          this.transitionDogSelect.disabled = false;
          this.transitionDogSelect.value = selectedDogId;
        }

        if (this.transitionPaceSelect) {
          this.transitionPaceSelect.disabled = false;
          if (!this.transitionPaceInitialized || !['standard', 'gentle'].includes(this.transitionPaceSelect.value)) {
            this.transitionPaceSelect.value = 'standard';
          }
          this.transitionPaceInitialized = true;
        }

        if (this.transitionEmpty) this.transitionEmpty.hidden = true;
        this.transitionPlan.hidden = false;
        this.renderSelectedTransitionPlan();
      }

      renderSelectedTransitionPlan() {
        if (!this.transitionDogSelect || !this.transitionPlan || !this.transitionResults?.length) return;
        const selected = this.transitionResults.find(({ dog }) => dog.id === this.transitionDogSelect.value)
          || this.transitionResults[0];
        const pace = this.transitionPaceSelect?.value === 'gentle' ? 'gentle' : 'standard';
        const plan = calculateTransitionPlan(selected.dailyStartOz, this.config.transitionPhases, pace);

        if (!plan.length) {
          this.transitionPlan.hidden = true;
          if (this.transitionEmpty) {
            this.transitionEmpty.hidden = false;
            this.transitionEmpty.textContent = 'A custom transition plan is not available right now.';
          }
          if (this.transitionPlanSummary) this.transitionPlanSummary.textContent = '';
          this.transitionStepCards.forEach((card) => { card.hidden = true; });
          return;
        }

        this.transitionPlan.hidden = false;
        if (this.transitionEmpty) this.transitionEmpty.hidden = true;
        const dogName = selected.dog.name || `Dog ${this.transitionResults.indexOf(selected) + 1}`;
        const dailyAmount = this.formatTransitionAmount(selected.dailyStartOz, selected.dog.unit);
        if (this.transitionPlanSummary) {
          this.transitionPlanSummary.textContent = `Plan shown for ${dogName}, starting at ${dailyAmount} per day. Per-meal amounts assume two equal meals.`;
        }

        const planBySourceIndex = new Map(plan.map((phase) => [phase.sourceIndex, phase]));
        this.transitionStepCards.forEach((card, index) => {
          const phase = planBySourceIndex.get(index);
          card.hidden = !phase;
          if (!phase) return;

          const days = card.querySelector('[data-rd-calculator-transition-days]');
          const rawAmount = card.querySelector('[data-rd-calculator-transition-raw-amount]');
          const mealAmount = card.querySelector('[data-rd-calculator-transition-meal-amount]');
          const currentFood = card.querySelector('[data-rd-calculator-transition-current]');
          if (days) days.textContent = phase.label;
          if (rawAmount) {
            rawAmount.textContent = this.formatTransitionAmount(phase.ounces, selected.dog.unit);
          }
          if (mealAmount) {
            mealAmount.textContent = `${this.formatTransitionAmount(phase.perMealOunces, selected.dog.unit)} per meal`;
          }
          if (currentFood) {
            currentFood.textContent = phase.currentFoodPercent > 0
              ? `${formatNumber(phase.currentFoodPercent, 0)}% of the usual daily portion`
              : 'None';
          }
        });
      }

      clearTransitionPlanner({ resetPace = false } = {}) {
        this.transitionResults = [];
        if (this.transitionDogSelect) {
          this.transitionDogSelect.disabled = true;
          if (this.transitionDogSelect.replaceChildren) {
            const option = browserWindow.document.createElement('option');
            option.value = '';
            option.textContent = 'Calculate above to personalize';
            this.transitionDogSelect.replaceChildren(option);
          }
        }
        if (this.transitionPaceSelect) {
          this.transitionPaceSelect.disabled = true;
          if (resetPace) this.transitionPaceSelect.value = 'standard';
        }
        if (resetPace) this.transitionPaceInitialized = false;
        if (this.transitionPlan) this.transitionPlan.hidden = true;
        if (this.transitionEmpty) this.transitionEmpty.hidden = false;
        if (this.transitionPlanSummary) this.transitionPlanSummary.textContent = '';
        (this.transitionStepCards || []).forEach((card) => {
          card.hidden = true;
          card.querySelector('[data-rd-calculator-transition-days]')?.replaceChildren();
          card.querySelector('[data-rd-calculator-transition-raw-amount]')?.replaceChildren();
          card.querySelector('[data-rd-calculator-transition-meal-amount]')?.replaceChildren();
          card.querySelector('[data-rd-calculator-transition-current]')?.replaceChildren();
        });
        this.transitionAmounts?.replaceChildren();
        this.updateTransitionToggleLabel();
      }

      updateTransitionToggleLabel() {
        if (!this.transitionSummaryText || !this.transitionDetails) return;
        this.transitionSummaryText.textContent = this.transitionDetails.open
          ? 'Hide custom transition plan'
          : 'Build my custom transition plan';
      }

      formatTransitionAmount(ounces, unit) {
        const formattedOunces = `${formatNumber(ounces)} oz`;
        const formattedGrams = `${formatNumber((ounces / 16) * GRAMS_PER_LB, 0)} g`;
        return unit === 'kg'
          ? `${formattedGrams} (${formattedOunces})`
          : `${formattedOunces} (${formattedGrams})`;
      }

      renderLegacyTransitionAmounts(results) {
        if (!this.transitionAmounts) return;
        this.transitionAmounts.replaceChildren();
        results.forEach(({ dog, ...calculated }, index) => {
          const article = browserWindow.document.createElement('article');
          const heading = browserWindow.document.createElement('h4');
          const list = browserWindow.document.createElement('dl');
          heading.textContent = dog.name || `Dog ${index + 1}`;

          calculateTransitionAmounts(calculated.dailyStartOz, this.config.transitionPhases).forEach((phase) => {
            const row = browserWindow.document.createElement('div');
            const term = browserWindow.document.createElement('dt');
            const description = browserWindow.document.createElement('dd');
            term.textContent = `${phase.label} · ${formatNumber(phase.rawPercent)}%`;
            const transitionAmount = dog.unit === 'kg'
              ? `${formatNumber((phase.ounces / 16) * GRAMS_PER_LB)} g`
              : `${formatNumber(phase.ounces)} oz`;
            description.textContent = `${transitionAmount} Raw Dawg per day`;
            row.append(term, description);
            list.appendChild(row);
          });

          article.append(heading, list);
          this.transitionAmounts.appendChild(article);
        });
      }

      dailyDisplay(calculated, unit) {
        if (unit === 'kg') {
          return {
            range: `${formatNumber(calculated.dailyLowLb * GRAMS_PER_LB)}–${formatNumber(calculated.dailyHighLb * GRAMS_PER_LB)} g/day`,
            start: `${formatNumber(calculated.dailyStartLb * GRAMS_PER_LB)} g/day`
          };
        }
        return {
          range: `${formatNumber(calculated.dailyLowOz)}–${formatNumber(calculated.dailyHighOz)} oz/day`,
          start: `${formatNumber(calculated.dailyStartOz)} oz/day`
        };
      }

      householdName() {
        const namedDogs = this.dogs.map((dog, index) => dog.name || `Dog ${index + 1}`);
        if (namedDogs.length === 1) return namedDogs[0];
        if (namedDogs.length === 2) return namedDogs.join(' and ');
        return `${namedDogs.slice(0, -1).join(', ')}, and ${namedDogs.at(-1)}`;
      }

      storageAllowed() {
        try {
          const privacy = browserWindow.Shopify?.customerPrivacy;
          if (typeof privacy?.preferencesProcessingAllowed === 'function') {
            return privacy.preferencesProcessingAllowed() === true;
          }
          return !browserWindow.Shopify;
        } catch (_error) {
          return false;
        }
      }

      restoreState() {
        if (!this.storageAllowed()) return null;
        try {
          const stored = JSON.parse(browserWindow.localStorage.getItem(STORAGE_KEY));
          if (!stored || ![1, STORAGE_VERSION].includes(stored.version) || Date.now() - stored.savedAt > STORAGE_TTL) {
            browserWindow.localStorage.removeItem(STORAGE_KEY);
            return null;
          }
          const dogs = Array.isArray(stored.dogs) ? stored.dogs.slice(0, Number(this.config.maxDogs || 8)) : [];
          return dogs.length ? dogs.map((dog) => this.createDog(dog)) : null;
        } catch (_error) {
          return null;
        }
      }

      saveState() {
        if (!this.storageAllowed()) {
          try { browserWindow.localStorage.removeItem(STORAGE_KEY); } catch (_error) { /* no-op */ }
          this.updateStorageNote();
          return;
        }
        try {
          browserWindow.localStorage.setItem(STORAGE_KEY, JSON.stringify({
            version: STORAGE_VERSION,
            savedAt: Date.now(),
            dogs: this.dogs
          }));
          this.updateStorageNote(true);
        } catch (_error) {
          this.updateStorageNote(false);
        }
      }

      updateStorageNote(saved = null) {
        if (!this.storageNote) return;
        if (!this.storageAllowed()) {
          this.storageNote.textContent = 'Your answers stay in this tab and are never emailed.';
        } else if (saved === false) {
          this.storageNote.textContent = 'Your plan works here, but this browser could not save it.';
        } else if (saved === true) {
          this.storageNote.textContent = 'Saved on this device only for 30 days.';
        } else {
          this.storageNote.textContent = 'Your answers can be saved on this device for 30 days.';
        }
      }

      reset() {
        try { browserWindow.localStorage.removeItem(STORAGE_KEY); } catch (_error) { /* no-op */ }
        this.dogs = [this.createDog()];
        this.hasCalculated = false;
        this.startedTracked = false;
        this.renderDogList();
        this.clearErrors();
        this.showEmptyResult(this.config.emptyState || 'Enter your dog’s details to see a plan.');
        if (this.transitionDetails) this.transitionDetails.open = false;
        this.updateStorageNote();
        this.dogList.querySelector('[data-rd-calculator-name]')?.focus();
        this.announce('Saved feeding plan cleared.');
      }

      trackStarted() {
        if (this.startedTracked) return;
        this.startedTracked = true;
        const units = [...new Set(this.dogs.map((dog) => dog.unit))];
        this.track('rawdawg_calculator_started', {
          unit: units.length === 1 ? units[0] : 'mixed',
          dog_count_bucket: dogCountBucket(this.dogs.length)
        });
      }

      trackCompleted(plan) {
        const units = [...new Set(this.dogs.map((dog) => dog.unit))];
        this.track('rawdawg_calculator_completed', {
          unit: units.length === 1 ? units[0] : 'mixed',
          dog_count_bucket: dogCountBucket(this.dogs.length),
          need_bucket: needBucket(plan.need28Lb),
          pack_signature: formatPackSignature(plan.packPlan.quantities)
        });
      }

      track(name, payload) {
        if (!this.config.basicAnalytics) return;
        try {
          const privacy = browserWindow.Shopify?.customerPrivacy;
          const analyticsAllowed = typeof privacy?.analyticsProcessingAllowed === 'function'
            && privacy.analyticsProcessingAllowed() === true;
          if (!analyticsAllowed || typeof browserWindow.Shopify?.analytics?.publish !== 'function') return;
          const published = browserWindow.Shopify.analytics.publish(name, payload);
          if (published?.catch) published.catch(() => {});
        } catch (_error) {
          // Analytics never blocks the calculator.
        }
      }

      announce(message) {
        if (!this.liveStatus) return;
        this.liveStatus.textContent = '';
        browserWindow.requestAnimationFrame(() => { this.liveStatus.textContent = message; });
      }
    }

    browserWindow.customElements.define('rd-feeding-calculator', RDFeedingCalculatorElement);
  };

  return {
    LB_PER_KG,
    calculateDog,
    calculateHousehold,
    calculateTransitionAmounts,
    calculateTransitionPlan,
    convertToPounds,
    formatPackOrderLabel,
    formatPackSignature,
    needBucket,
    normalizeStoredDog,
    optimizeBoxes,
    register
  };
});
